import React, { memo, useEffect, useMemo } from 'react';
import { Panel } from 'primereact/panel';
import WheelPhotoThumbnailGrid from './WheelPhotoThumbnailGrid';
import WheelFieldLabel from './WheelFieldLabel';
import WheelLocationTable from './WheelLocationTable';
import {
  WHEEL_POSITIONS,
  WHEEL_LOCATION_ITEMS,
  WHEEL_LOCATION_PHOTO_KEYS,
  WHEEL_PRE_PHOTOS_PER_POSITION,
  locationPhotoPart,
} from '../../constants/WheelInspectionConstants';
import { getPositionLabel, applyLocationDetailDefaultsForPosition } from '../../utils/wheelInspectionUtil';

const LOCATION_PHOTO_ITEMS = WHEEL_LOCATION_ITEMS.filter((item) =>
  WHEEL_LOCATION_PHOTO_KEYS.includes(item.key)
);

const LocationPhotoBlock = ({
  position,
  fileItems,
  setFileItems,
  getFileURL,
  setLoading,
  readOnly,
}) => (
  <div className="flex flex-wrap gap-3 align-items-end border-1 border-200 border-round p-3">
    <span className="text-sm font-semibold white-space-nowrap w-full sm:w-auto mb-1 sm:mb-0">
      {getPositionLabel(position)}
    </span>
    {LOCATION_PHOTO_ITEMS.map((item) => (
      <div
        key={`${position}_${item.key}`}
        className="flex flex-column align-items-center gap-1"
      >
        <span className="text-sm text-color-secondary">{item.title}</span>
        <WheelPhotoThumbnailGrid
          partCode={locationPhotoPart(position, item.key)}
          fileItems={fileItems}
          setFileItems={setFileItems}
          getFileURL={getFileURL}
          maxFiles={WHEEL_PRE_PHOTOS_PER_POSITION}
          multiple={false}
          disabled={readOnly}
          setLoading={setLoading}
        />
      </div>
    ))}
  </div>
);

const WheelLocationDetailPanel = ({
  control,
  setValue,
  getValues,
  selectedPositions = [],
  fileItems,
  setFileItems,
  getFileURL,
  setLoading,
  readOnly = false,
}) => {
  const activePositions = useMemo(
    () => WHEEL_POSITIONS.filter((p) => selectedPositions.includes(p.code)),
    [selectedPositions]
  );

  useEffect(() => {
    if (!setValue || readOnly) return;
    selectedPositions.forEach((pos) => {
      applyLocationDetailDefaultsForPosition(setValue, pos, getValues);
    });
  }, [selectedPositions, setValue, getValues, readOnly]);

  if (!activePositions.length) {
    return (
      <Panel header="복원후 위치별 상세점검 및 측정(안전검사)" className="shadow-2">
        <p className="text-color-secondary m-0">
          휠 복원 전 사진 기록에서 위치를 선택하면 점검 항목이 표시됩니다.
        </p>
      </Panel>
    );
  }

  return (
    <Panel header="복원후 위치별 상세점검 및 측정(안전검사)" className="shadow-2">
      <WheelLocationTable
        positions={activePositions}
        control={control}
        readOnly={readOnly}
      />

      <div className="mt-3">
        <WheelFieldLabel>크랙 · 굴절 · 바란스 · 도색 — 사진</WheelFieldLabel>
        <p className="text-sm text-color-secondary mb-2">
          위치별로 항목당 사진 1장씩 등록할 수 있습니다. (선택, 총 4장)
        </p>
        <div className="flex flex-column gap-3">
          {activePositions.map((p) => (
            <LocationPhotoBlock
              key={p.code}
              position={p.code}
              fileItems={fileItems}
              setFileItems={setFileItems}
              getFileURL={getFileURL}
              setLoading={setLoading}
              readOnly={readOnly}
            />
          ))}
        </div>
      </div>
    </Panel>
  );
};

export default memo(WheelLocationDetailPanel);
